import { useEffect } from 'react';
import IconModal from '../IconModal/IconModal';
import TextModal from '../TextModal/TextModal';
import GenericModal from '../GenericModal/GenericModal';
import LoadingSpinner from '../../../atoms/loaders/LoadingSpinner/LoadingSpinner';

export interface ModalSettingsShape {
  headerBackground?: boolean;
  title?: string;
  text?: string;
  primaryButtonText?: string;
  outlineButtonText?: string;
  loading?: boolean;
  handleClose?: () => any;
  handlePrimaryClick?: () => any;
  handleOutlineClick?: () => any;
  handleBackPress?: () => any;
  childrenClassName?: string;
}

export type ModalProps = {
  kind: 'pop-up' | 'info' | 'search' | 'tc' | 'generic';
  settings: ModalSettingsShape;
  isFullScreen?: boolean;
  isLoading?: boolean;
  children?: React.ReactNode;
  className?: string;
};

function renderChildren({
  kind,
  settings,
  children,
  isLoading,
  className = 'bg-white max-w-[90vw] md:w-3/4 lg:w-1/4 lg:max-w-[350px] w-full rounded-md h-fit',
}: ModalProps) {
  if (kind === 'pop-up')
    return (
      <div className={className}>
        <IconModal kind="pop-up" settings={settings} />
      </div>
    );

  if (kind === 'search')
    return (
      <div className={className}>
        <IconModal kind="search" settings={settings} />
      </div>
    );

  if (kind === 'info')
    return (
      <div className={className}>
        <TextModal kind="info" settings={settings} />
      </div>
    );

  if (kind === 'tc')
    return (
      <div className={className}>
        <TextModal kind="tc" settings={settings} />
      </div>
    );

  if (kind === 'generic' && children)
    return (
      // Generic modal
      <div
        role="dialog"
        className={
          'relative flex h-full w-full flex-col bg-white lg:h-fit lg:max-h-[90vh] lg:rounded-md ' +
          className
        }
      >
        {isLoading && <LoadingSpinner overlay={true} size="sm" />}
        <GenericModal settings={settings}>{children}</GenericModal>
      </div>
    );

  return null;
}

// Background overlay for full screen modals
function FullScreen({ children }: any) {
  return (
    <div className="fixed inset-0 z-50 flex flex-wrap items-center justify-center overflow-y-auto bg-gray-500/50 lg:p-4">
      {children}
    </div>
  );
}

export function Modal({
  kind,
  settings,
  isFullScreen = true,
  isLoading,
  children,
  className,
}: ModalProps) {
  // Add Tailwind class to body to prevent scroll when modal is open
  useEffect(() => {
    document.body.classList.add('overflow-hidden');

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, []);

  const content = renderChildren({
    kind,
    settings,
    children,
    className,
    isLoading,
  });

  return isFullScreen ? <FullScreen>{content}</FullScreen> : content;
}

export default Modal;
